import React, { useState, useEffect, useMemo } from "react";
import Loader from "../../Shared/Loader";
import "../../../css/CenterManagementDashboard.css";
import api from "../../../api/axiosConfig";

interface Center {
  _id: string;
  name: string;
  centerType?: string;
  city?: string;
  [key: string]: any;
}

interface CentersApiResponse {
  success: boolean;
  data: Center[];
  total?: number;
  page?: number;
  limit?: number;
}

export interface ReleasedSlotItem {
  _id: string;
  appointment_cluster_id: string;
  center_id: string;
  slot_template_id: string;
  date: string;
  start_time: string;
  end_time: string;
  slot_type: string;
  total_appointments: number;
  available_appointments: number;
  weekday_template_map?: Record<string, string>;
  [key: string]: any;
}

interface DateAggregatedData {
  dateStr: string;
  totalAppointments: number;
  availableAppointments: number;
  slots: ReleasedSlotItem[];
}

export interface StaffAssignment {
  userId: string;
  userName: string;
}

export interface BookingDetails {
  booking_id: string;
  child_id: string;
  child_name: string;
  child_age?: number;
  child_profile?: string;
  emrId?: string;
  paymentId?: string;
}

export interface DetailedSlot {
  slot_id: string;
  timings?: string;
  start_time: string;
  end_time: string;
  total_appointments: number;
  available_appointments: number;
  booked_appointments: number;
  assigned_staffs?: StaffAssignment[];
  center_admin?: StaffAssignment[];
  bookings?: BookingDetails[];
  slot_type?: string;
}

export interface DetailedSlotsResponse {
  success: boolean;
  center_id: string;
  date: string;
  slots: DetailedSlot[];
}

const monthsList = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const CenterManagementDashboard: React.FC = () => {
  const [centers, setCenters] = useState<Center[]>([]);
  const [selectedCenterId, setSelectedCenterId] = useState<string>("");
  const [isLoadingCenters, setIsLoadingCenters] = useState<boolean>(true);
  const [centersError, setCentersError] = useState<string | null>(null);

  const [releasedSlots, setReleasedSlots] = useState<ReleasedSlotItem[]>([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState<boolean>(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);

  // Calendar Date State (default to current month/year)
  const [currentDate, setCurrentDate] = useState<Date>(new Date());

  // Selected date for slot detail modal
  const [selectedDateDetail, setSelectedDateDetail] = useState<DateAggregatedData | null>(null);

  // Detailed slot API response state
  const [detailedSlots, setDetailedSlots] = useState<DetailedSlot[]>([]);
  const [isLoadingDetailedSlots, setIsLoadingDetailedSlots] = useState<boolean>(false);
  const [detailedSlotsError, setDetailedSlotsError] = useState<string | null>(null);

  // Fetch Centers List
  useEffect(() => {
    const fetchCenters = async () => {
      try {
        setIsLoadingCenters(true);
        const token = sessionStorage.getItem("accessToken");
        const res = await api.center_api.get<CentersApiResponse>("/api/v1/centers", {
          headers: { Authorization: `Bearer ${token}` },
        });

        const centerList = res.data?.data || [];
        setCenters(centerList);
        if (centerList.length > 0) {
          setSelectedCenterId(centerList[0]._id);
        }
      } catch (err: any) {
        console.error("Failed to fetch centers:", err);
        setCentersError(err.message || "Could not load centers list.");
      } finally {
        setIsLoadingCenters(false);
      }
    };

    fetchCenters();
  }, []);

  // Fetch Released Slots when selected center changes
  useEffect(() => {
    if (!selectedCenterId) return;

    const fetchReleasedSlots = async () => {
      try {
        setIsLoadingSlots(true);
        setSlotsError(null);
        const token = sessionStorage.getItem("accessToken");
        const res = await api.appointment_api.get<ReleasedSlotItem[]>(
          `/api/released-slots/center/${selectedCenterId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const data = Array.isArray(res.data) ? res.data : (res.data as any)?.data || [];
        setReleasedSlots(data);
      } catch (err: any) {
        console.error("Failed to fetch released slots for center:", err);
        const statusCode = err.response?.status;
        const serverMsg = err.response?.data?.message || err.response?.data?.error || "";

        // If center configuration is pending or 400/404 error occurs, treat gracefully as 0 released slots without error banner
        if (
          statusCode === 400 ||
          statusCode === 404 ||
          serverMsg.toLowerCase().includes("configuration is pending") ||
          serverMsg.toLowerCase().includes("pending")
        ) {
          setSlotsError(null);
          setReleasedSlots([]);
        } else {
          setSlotsError(serverMsg || err.message || "Could not load released slots.");
          setReleasedSlots([]);
        }
      } finally {
        setIsLoadingSlots(false);
      }
    };

    fetchReleasedSlots();
  }, [selectedCenterId]);

  // Fetch Detailed Slot Information when a date modal opens
  useEffect(() => {
    if (!selectedDateDetail || !selectedCenterId) {
      setDetailedSlots([]);
      return;
    }

    const fetchSlotDetails = async () => {
      try {
        setIsLoadingDetailedSlots(true);
        setDetailedSlotsError(null);
        const token = sessionStorage.getItem("accessToken");
        const res = await api.appointment_api.get<DetailedSlotsResponse>(
          `/api/appointments/center/${selectedCenterId}/date/${selectedDateDetail.dateStr}/slots-details`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (res.data && Array.isArray(res.data.slots)) {
          setDetailedSlots(res.data.slots);
        } else {
          setDetailedSlots([]);
        }
      } catch (err: any) {
        console.error("Failed to fetch detailed slot information:", err);
        const statusCode = err.response?.status;
        const serverMsg = err.response?.data?.message || err.response?.data?.error || "";

        if (
          statusCode === 400 ||
          statusCode === 404 ||
          serverMsg.toLowerCase().includes("configuration is pending") ||
          serverMsg.toLowerCase().includes("pending")
        ) {
          setDetailedSlotsError(null);
          setDetailedSlots([]);
        } else {
          setDetailedSlotsError(serverMsg || err.message || "Could not load detailed slot information.");
          setDetailedSlots([]);
        }
      } finally {
        setIsLoadingDetailedSlots(false);
      }
    };

    fetchSlotDetails();
  }, [selectedDateDetail, selectedCenterId]);

  // Selected Center Details
  const selectedCenterInfo = useMemo(() => {
    return centers.find((c) => c._id === selectedCenterId) || null;
  }, [centers, selectedCenterId]);

  // Aggregate released slots by date string (YYYY-MM-DD)
  const slotsByDateMap = useMemo(() => {
    const map = new Map<string, DateAggregatedData>();

    releasedSlots.forEach((slot) => {
      if (!slot.date) return;

      const dateKey = slot.date.split("T")[0];

      if (!map.has(dateKey)) {
        map.set(dateKey, {
          dateStr: dateKey,
          totalAppointments: 0,
          availableAppointments: 0,
          slots: [],
        });
      }

      const existing = map.get(dateKey)!;
      existing.totalAppointments += Number(slot.total_appointments || 0);
      existing.availableAppointments += Number(slot.available_appointments || 0);
      existing.slots.push(slot);
    });

    return map;
  }, [releasedSlots]);

  // Summary counts for the modal popover
  const modalSummaryStats = useMemo(() => {
    if (detailedSlots.length > 0) {
      const total = detailedSlots.reduce((sum, s) => sum + Number(s.total_appointments || 0), 0);
      const available = detailedSlots.reduce((sum, s) => sum + Number(s.available_appointments || 0), 0);
      const booked = detailedSlots.reduce((sum, s) => sum + Number(s.booked_appointments || 0), 0);
      return { total, available, booked };
    }
    if (selectedDateDetail) {
      const total = selectedDateDetail.totalAppointments;
      const available = selectedDateDetail.availableAppointments;
      const booked = total - available;
      return { total, available, booked };
    }
    return { total: 0, available: 0, booked: 0 };
  }, [detailedSlots, selectedDateDetail]);

  // Calendar Matrix Calculation
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfWeek = new Date(year, month, 1).getDay();

  const handlePrevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  // Helper to format YYYY-MM-DD
  const formatYYYYMMDD = (y: number, m: number, d: number) => {
    const mm = String(m + 1).padStart(2, "0");
    const dd = String(d).padStart(2, "0");
    return `${y}-${mm}-${dd}`;
  };

  // Helper to pick color class based on availability ratio
  const getAvailabilityClass = (avail: number, total: number) => {
    if (total === 0) return "no-slots";
    if (avail === 0) return "avail-red";
    const ratio = avail / total;
    if (ratio === 1) return "avail-green-100";
    if (ratio >= 0.75) return "avail-green-75";
    if (ratio >= 0.4) return "avail-green-40";
    return "avail-yellow";
  };

  return (
    <div className="center-dashboard-container">
      {/* Top Header & Controls */}
      <div className="center-dashboard-header">
        <div className="header-title-section">
          <h2>Center Appointments Dashboard</h2>
          <p className="subtitle">
            View released slot availability and appointment capacity per center
          </p>
        </div>

        {/* Center Dropdown Selector */}
        <div className="center-select-box">
          <label htmlFor="centerSelect">Select Center:</label>
          {isLoadingCenters ? (
            <Loader inline size="small" text="Loading centers..." />
          ) : (
            <select
              id="centerSelect"
              value={selectedCenterId}
              onChange={(e) => setSelectedCenterId(e.target.value)}
              className="center-dropdown"
            >
              {centers.length === 0 && <option value="">No centers available</option>}
              {centers.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.name} {c.city ? `(${c.city})` : ""}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Selected Center Summary Bar */}
      {selectedCenterInfo && (
        <div className="center-info-banner">
          <div className="info-badge-item">
            <span className="info-label">Center Name</span>
            <span className="info-val">{selectedCenterInfo.name}</span>
          </div>
          <div className="info-badge-item">
            <span className="info-label">Type</span>
            <span className="info-val">{selectedCenterInfo.centerType || "QAVACH CENTER"}</span>
          </div>
          <div className="info-badge-item">
            <span className="info-label">City</span>
            <span className="info-val">{selectedCenterInfo.city || "N/A"}</span>
          </div>
          <div className="info-badge-item">
            <span className="info-label">Total Released Dates</span>
            <span className="info-val highlight">{slotsByDateMap.size} Days</span>
          </div>
        </div>
      )}

      {/* Color Legend Bar */}
      <div className="dashboard-legend-bar">
        <span className="legend-title">Availability Legend:</span>
        <div className="legend-item">
          <span className="color-box avail-green-100"></span>
          <span>100% Available</span>
        </div>
        <div className="legend-item">
          <span className="color-box avail-green-75"></span>
          <span>75% - 99% Available</span>
        </div>
        <div className="legend-item">
          <span className="color-box avail-green-40"></span>
          <span>40% - 74% Available</span>
        </div>
        <div className="legend-item">
          <span className="color-box avail-yellow"></span>
          <span>1% - 39% Available</span>
        </div>
        <div className="legend-item">
          <span className="color-box avail-red"></span>
          <span>0 Available (Fully Booked)</span>
        </div>
        <div className="legend-item">
          <span className="color-box no-slots"></span>
          <span>No Slots</span>
        </div>
      </div>

      {/* Calendar Section */}
      <div className="calendar-card">
        {/* Calendar Nav Header */}
        <div className="calendar-nav-bar">
          <div className="nav-month-title">
            <select
              className="calendar-month-select"
              value={month}
              onChange={(e) => setCurrentDate(new Date(year, parseInt(e.target.value), 1))}
            >
              {monthsList.map((mName, mIdx) => (
                <option key={mName} value={mIdx}>
                  {mName}
                </option>
              ))}
            </select>

            <select
              className="calendar-year-select"
              value={year}
              onChange={(e) => setCurrentDate(new Date(parseInt(e.target.value), month, 1))}
            >
              {Array.from({ length: 16 }, (_, i) => 2024 + i).map((yNum) => (
                <option key={yNum} value={yNum}>
                  {yNum}
                </option>
              ))}
            </select>
          </div>
          <div className="nav-action-buttons">
            <button className="btn-nav" onClick={handlePrevMonth}>
              &lt; Prev
            </button>
            <button className="btn-today" onClick={handleToday}>
              Today
            </button>
            <button className="btn-nav" onClick={handleNextMonth}>
              Next &gt;
            </button>
          </div>
        </div>

        {isLoadingSlots ? (
          <div className="calendar-loader-box">
            <Loader text="Loading released appointments calendar..." />
          </div>
        ) : slotsError ? (
          <div className="calendar-error-box">{slotsError}</div>
        ) : (
          <div className="calendar-grid-wrapper">
            {/* Weekdays Header */}
            <div className="calendar-weekdays">
              {daysOfWeek.map((day) => (
                <div key={day} className="weekday-header-cell">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Month Days Grid */}
            <div className="calendar-days-grid">
              {/* Empty leading cells */}
              {Array.from({ length: firstDayOfWeek }).map((_, idx) => (
                <div key={`empty-${idx}`} className="calendar-day-cell empty"></div>
              ))}

              {/* Days of month */}
              {Array.from({ length: daysInMonth }).map((_, idx) => {
                const dayNum = idx + 1;
                const dateKey = formatYYYYMMDD(year, month, dayNum);
                const dayData = slotsByDateMap.get(dateKey);

                const isToday =
                  new Date().getDate() === dayNum &&
                  new Date().getMonth() === month &&
                  new Date().getFullYear() === year;

                const colorClass = dayData
                  ? getAvailabilityClass(dayData.availableAppointments, dayData.totalAppointments)
                  : "";

                return (
                  <div
                    key={dayNum}
                    className={`calendar-day-cell ${isToday ? "today" : ""} ${
                      dayData ? "has-data" : ""
                    }`}
                    onClick={() => dayData && setSelectedDateDetail(dayData)}
                  >
                    <div className="day-number-row">
                      <span className="day-num">{dayNum}</span>
                      {isToday && <span className="today-badge">Today</span>}
                    </div>

                    {dayData ? (
                      <div className={`day-slot-card ${colorClass}`}>
                        <div className="slot-count-row">
                          <span className="slot-label">Total:</span>
                          <span className="slot-val">{dayData.totalAppointments}</span>
                        </div>
                        <div className="slot-count-row">
                          <span className="slot-label">Available:</span>
                          <span className="slot-val font-bold">
                            {dayData.availableAppointments} / {dayData.totalAppointments}
                          </span>
                        </div>
                        <div className="slot-percentage-bar">
                          <div
                            className="bar-fill"
                            style={{
                              width: `${Math.round(
                                (dayData.availableAppointments / dayData.totalAppointments) * 100
                              )}%`,
                            }}
                          ></div>
                        </div>
                      </div>
                    ) : (
                      <div className="no-slot-text">&mdash;</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Slot Details Modal for Selected Date */}
      {selectedDateDetail && (
        <div className="modal-backdrop-slot-detail" onClick={() => setSelectedDateDetail(null)}>
          <div
            className="slot-detail-modal-container"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Full-bleed Header */}
            <div className="slot-detail-header">
              <h3>
                Slot Details &mdash; {selectedDateDetail.dateStr}
              </h3>
              <button
                className="global-close-btn"
                onClick={() => setSelectedDateDetail(null)}
              >
                ✕
              </button>
            </div>

            {/* Scrollable Modal Body */}
            <div className="slot-detail-body">
              {/* Summary Cards */}
              <div className="slot-detail-summary-bar">
                <div className="summary-stat-card">
                  <span className="stat-label">Total Appointments</span>
                  <span className="stat-val">{modalSummaryStats.total}</span>
                </div>
                <div className="summary-stat-card available">
                  <span className="stat-label">Available</span>
                  <span className="stat-val green">{modalSummaryStats.available}</span>
                </div>
                <div className="summary-stat-card booked">
                  <span className="stat-label">Booked</span>
                  <span className="stat-val red">{modalSummaryStats.booked}</span>
                </div>
              </div>

              <h4 className="sub-title">Slots Breakdown</h4>

              {isLoadingDetailedSlots ? (
                <div style={{ padding: "40px 0", textAlign: "center" }}>
                  <Loader text="Fetching detailed slot & booking information..." />
                </div>
              ) : detailedSlotsError ? (
                <div style={{ color: "#dc2626", padding: "20px 0", textAlign: "center" }}>
                  {detailedSlotsError}
                </div>
              ) : (
                <div className="slots-list-container">
                  {(detailedSlots.length > 0 ? detailedSlots : selectedDateDetail.slots).map((slot: any, index: number) => {
                    const avail = Number(slot.available_appointments || 0);
                    const total = Number(slot.total_appointments || 0);
                    const booked = Number(slot.booked_appointments ?? (total - avail));
                    const slotColorClass = getAvailabilityClass(avail, total);

                    const timingsText = slot.timings || `${slot.start_time} - ${slot.end_time}`;
                    const assignedStaff = slot.assigned_staffs || [];
                    const centerAdmins = slot.center_admin || [];
                    const bookingsList = slot.bookings || [];

                    return (
                      <div key={slot.slot_id || slot._id || index} className={`slot-item-card-expanded ${slotColorClass}`}>
                        {/* Slot Header */}
                        <div className="slot-card-header">
                          <div className="slot-time-col">
                            <span className="time-icon">⏰</span>
                            <span className="time-text">{timingsText}</span>
                            {slot.slot_type && <span className="slot-type-badge">{slot.slot_type}</span>}
                          </div>
                          <div className="slot-counts-col">
                            <div>
                              Available: <strong>{avail}</strong> / {total} &bull; Booked: <strong>{booked}</strong>
                            </div>
                            <div>
                              Status:{" "}
                              <span className="status-text font-bold">
                                {avail === 0
                                  ? "Fully Booked"
                                  : avail === total
                                  ? "100% Available"
                                  : "Partially Booked"}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Assigned Staff Row if available */}
                        {(assignedStaff.length > 0 || centerAdmins.length > 0) && (
                          <div className="slot-staff-row">
                            {assignedStaff.length > 0 && (
                              <div className="staff-group">
                                <span className="staff-label">👥 Assigned Staff:</span>
                                <div className="staff-pills">
                                  {assignedStaff.map((st: StaffAssignment, i: number) => (
                                    <span key={i} className="staff-pill">{st.userName || st.userId}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {centerAdmins.length > 0 && (
                              <div className="staff-group">
                                <span className="staff-label">🛡️ Center Admin:</span>
                                <div className="staff-pills">
                                  {centerAdmins.map((admin: StaffAssignment, i: number) => (
                                    <span key={i} className="staff-pill admin">{admin.userName || admin.userId}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Bookings Section */}
                        <div className="slot-bookings-box">
                          <div className="bookings-box-header">
                            <span>Bookings ({bookingsList.length})</span>
                          </div>
                          {bookingsList.length > 0 ? (
                            <table className="bookings-table">
                              <thead>
                                <tr>
                                  <th>Child Name</th>
                                  <th>EMR ID</th>
                                  <th>Age</th>
                                  <th>Payment ID</th>
                                </tr>
                              </thead>
                              <tbody>
                                {bookingsList.map((b: BookingDetails, bIdx: number) => (
                                  <tr key={b.booking_id || bIdx}>
                                    <td className="font-bold">{b.child_name || "N/A"}</td>
                                    <td><span className="emr-code-badge">{b.emrId || b.child_id || "N/A"}</span></td>
                                    <td>{b.child_age !== undefined ? `${b.child_age} yrs` : "N/A"}</td>
                                    <td>{b.paymentId || "N/A"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <div className="no-bookings-text">No bookings for this slot.</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Full-bleed Footer */}
            <div className="slot-detail-footer">
              <button
                className="btn-close-detail"
                onClick={() => setSelectedDateDetail(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CenterManagementDashboard;
